import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import type { MePublic } from "../lib/types";

interface AcceptBody {
  token: string;
  password: string;
  display_name: string;
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const mut = useMutation({
    mutationFn: (body: AcceptBody) =>
      api.post<MePublic>("/api/auth/accept", body),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      nav("/", { replace: true });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Something went wrong.");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("This invitation link is missing its token.");
      return;
    }
    mut.mutate({ token, password, display_name: displayName });
  };

  if (!token) {
    return (
      <AuthLayout
        kicker="N° — Invitation"
        title={
          <>
            A <span className="display-italic">missing</span> page
          </>
        }
        tagline="This invitation link doesn't include a token."
      >
        <div className="space-y-6">
          <p className="font-body text-lg italic text-ink-walnut animate-fade-up">
            Ask whoever invited you to share the full link.
          </p>
          <Link
            to="/login"
            className="label ink-link text-ink animate-fade-up"
            style={{ animationDelay: "100ms" }}
          >
            Back to entry
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      kicker="N° 03 — Invitation"
      title={
        <>
          You've been <span className="display-italic">invited</span>
        </>
      }
      tagline="Choose a name and a password to join the household."
    >
      <form onSubmit={onSubmit} className="space-y-8">
        <Field
          label="Your name"
          value={displayName}
          onChange={setDisplayName}
          required
          autoComplete="name"
          delay={320}
        />
        <Field
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
          required
          delay={400}
        />

        {error && (
          <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
            {error}
          </div>
        )}

        <div
          className="flex flex-wrap items-center gap-8 pt-2 animate-fade-up"
          style={{ animationDelay: "480ms" }}
        >
          <button type="submit" className="btn-ink" disabled={mut.isPending}>
            {mut.isPending ? "Pulling up a chair…" : "Accept the invitation"}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
